import { CheckCircle, Download, Calendar, MapPin, Users, Mail } from "lucide-react";
import type React from "react";
import { useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";

import { Badge } from "../../../components/ui/badge";
import { Button } from "../../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { Separator } from "../../../components/ui/separator";
import { useGetBookingByIdQuery } from "../../../store/api/bookingApi";
import { ROUTES } from "../../../utils/constants";
import { formatCurrency, formatDateTime, generateBookingReference } from "../../../utils/helpers";

const Confirmation: React.FC = () => {
  const [searchParams] = useSearchParams();
  const bookingId = searchParams.get("bookingId");
  const { data: bookingData, isLoading } = useGetBookingByIdQuery(bookingId!);

  const booking = bookingData?.data;

  useEffect(() => {
    // Clear selected seats from store after successful booking
    // This would be handled by the booking slice
  }, []);

  const handleDownloadTicket = () => {
    // Generate and download ticket PDF
    console.info("Downloading ticket...");
  };

  const handleEmailTicket = () => {
    // Send ticket via email
    console.info("Emailing ticket...");
  };

  if (isLoading) {
    return (
      <div className='container py-8'>
        <div className='mx-auto max-w-2xl text-center'>
          <div className='mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-b-2 border-primary'></div>
          <p>Processing your booking...</p>
        </div>
      </div>
    );
  }

  if (!booking) {
    return (
      <div className='container py-8'>
        <div className='mx-auto max-w-2xl text-center'>
          <h1 className='mb-4 text-2xl font-bold'>Booking Not Found</h1>
          <Button asChild>
            <Link to={ROUTES.HOME}>Go Home</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className='container py-8'>
      <div className='mx-auto max-w-2xl'>
        {/* Success Header */}
        <div className='mb-8 text-center'>
          <div className='mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-100'>
            <CheckCircle className='h-8 w-8 text-green-600' />
          </div>
          <h1 className='mb-2 text-3xl font-bold text-green-600'>Booking Confirmed!</h1>
          <p className='text-muted-foreground'>
            Your booking has been successfully processed. You&apos;ll receive a confirmation email
            shortly.
          </p>
        </div>

        {/* Booking Details */}
        <Card className='mb-6'>
          <CardHeader>
            <CardTitle>Booking Details</CardTitle>
          </CardHeader>
          <CardContent className='space-y-4'>
            <div className='flex items-center justify-between'>
              <span className='text-sm text-muted-foreground'>Booking Reference</span>
              <Badge variant='outline' className='font-mono'>
                {booking.bookingReference || generateBookingReference()}
              </Badge>
            </div>

            <div className='flex items-center justify-between'>
              <span className='text-sm text-muted-foreground'>Payment Status</span>
              <Badge variant={booking.paymentStatus === "completed" ? "default" : "secondary"}>
                {booking.paymentStatus}
              </Badge>
            </div>

            <Separator />

            <div>
              <h3 className='mb-2 font-semibold'>{booking.event?.title}</h3>
              <div className='space-y-2 text-sm text-muted-foreground'>
                <div className='flex items-center gap-2'>
                  <Calendar className='h-4 w-4' />
                  <span>{formatDateTime(booking.event?.datetime || "")}</span>
                </div>
                <div className='flex items-center gap-2'>
                  <MapPin className='h-4 w-4' />
                  <span>{booking.event?.ground?.location.city}</span>
                </div>
                <div className='flex items-center gap-2'>
                  <Users className='h-4 w-4' />
                  <span>
                    {booking.seats.length} seat{booking.seats.length !== 1 ? "s" : ""}
                  </span>
                </div>
              </div>
            </div>

            <Separator />

            <div className='space-y-2'>
              <h4 className='font-medium'>Selected Seats</h4>
              <div className='grid grid-cols-4 gap-2'>
                {booking.seats?.map((seat, index) => (
                  <Badge key={index} variant='secondary' className='justify-center'>
                    {seat}
                  </Badge>
                ))}
              </div>
            </div>

            <Separator />

            <div className='flex justify-between font-semibold'>
              <span>Total Paid</span>
              <span>{formatCurrency(booking.totalAmount)}</span>
            </div>
          </CardContent>
        </Card>

        {/* Actions */}
        <div className='mb-6 grid grid-cols-1 gap-4 md:grid-cols-2'>
          <Button onClick={handleDownloadTicket} className='flex items-center gap-2'>
            <Download className='h-4 w-4' />
            Download Ticket
          </Button>
          <Button
            variant='outline'
            onClick={handleEmailTicket}
            className='flex items-center gap-2 bg-transparent'
          >
            <Mail className='h-4 w-4' />
            Email Ticket
          </Button>
        </div>

        {/* Next Steps */}
        <Card>
          <CardHeader>
            <CardTitle>What&apos;s Next?</CardTitle>
          </CardHeader>
          <CardContent className='space-y-3 text-sm'>
            <div className='flex items-start gap-3'>
              <div className='mt-2 h-2 w-2 flex-shrink-0 rounded-full bg-primary'></div>
              <div>
                <p className='font-medium'>Check your email</p>
                <p className='text-muted-foreground'>
                  We&apos;ve sent your tickets and booking confirmation
                </p>
              </div>
            </div>
            <div className='flex items-start gap-3'>
              <div className='mt-2 h-2 w-2 flex-shrink-0 rounded-full bg-primary'></div>
              <div>
                <p className='font-medium'>Arrive early</p>
                <p className='text-muted-foreground'>Gates open 1 hour before the event starts</p>
              </div>
            </div>
            <div className='flex items-start gap-3'>
              <div className='mt-2 h-2 w-2 flex-shrink-0 rounded-full bg-primary'></div>
              <div>
                <p className='font-medium'>Bring your ticket</p>
                <p className='text-muted-foreground'>
                  Show your digital or printed ticket at the entrance
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Navigation */}
        <div className='mt-8 flex justify-center gap-4'>
          <Button variant='outline' asChild>
            <Link to={ROUTES.DASHBOARD.FAN}>View My Bookings</Link>
          </Button>
          <Button asChild>
            <Link to={ROUTES.EVENTS}>Book Another Event</Link>
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Confirmation;
